import { GateMovementState2 } from '@/__generated__/graphql';
import { useFormatters } from '@/hooks/useFormatters';
import { AU_SUCCESS, AU_TINT, AU_TEXT_DIM } from '@/theme/au-compat';
import { useQuery } from '@apollo/client';
import ApolloErrorBoundary from '@components/ApolloErrorBoundary';
import { BatteryLevelIcon } from '@components/BatteryLevelIcon';
import { Access, useSuspenseAuthorization } from '@features/authorization';
import { BATTERY_LOW_THRESHOLD } from '@features/gateCommon/utils/battery';
import { AwaitingDevice } from '@features/moveGateModal/components/AwaitingDevice';
import { useGateLiveStatus } from '@features/moveGateModal/hooks/useGateLiveStatus';
import { Trans } from '@lingui/macro';
import { BatteryUnknown } from '@mui/icons-material';
import { Box, Skeleton } from '@mui/material';
import {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type FC,
  type ReactNode,
} from 'react';
import { GET_GATE_READINGS } from '../graphql/queries/getGateReadings';

interface Props {
  movementId: number | null;
}

const POLLING_INTERVAL = 30_000;

const NoFallback = () => null;

/**
 * `.oc-conn` — la tira de conexión y batería del encabezado del modal.
 *
 * **Va dentro del título, no en un panel propio.** Era el área `status` de la grilla, o sea una
 * card al lado del nombre de la compuerta; en el espécimen es parte de la cabecera del diálogo, a
 * la derecha del título (`modal-frame.tsx § ConnectionStatus`). La diferencia no es de gusto: un
 * panel es una unidad de contenido y esto es el encabezado de la ventana, así que darle card lo
 * ponía al mismo nivel que el resumen o el histórico.
 *
 * Conexión y batería salen de la caché de última lectura del backend, la misma que leen el widget y
 * «Definir operación», y se muestran compactas, sin antigüedad (`modal-frame.tsx`). Con la
 * solicitud en cola se consulta al equipo una vez al abrir y, mientras responde, la conexión dice
 * «Esperando al equipo…» en vez de presentar el dato registrado como vigente.
 */
export const ConnectionStatus: FC<Props> = ({ movementId }) => {
  if (!movementId) return null;
  return <Readings movementId={movementId} />;
};

const Readings: FC<{ movementId: number }> = ({ movementId }) => {
  const { formatMetricValue } = useFormatters();
  // `useQuery` y no una suspense query: el polling no debe remontar la consulta al equipo de abajo.
  // `network-only`: al reabrir, un `IN_QUEUE` cacheado no presenta datos viejos ni dispara la
  // consulta al equipo. Un poll fallido deja lo último leído en vez de vaciar la tira.
  const {
    data: freshData,
    previousData,
    error,
    refetch,
  } = useQuery(GET_GATE_READINGS, {
    variables: { movementId },
    pollInterval: POLLING_INTERVAL,
    fetchPolicy: 'network-only',
  });
  const data = freshData ?? previousData;
  const [awaiting, setAwaiting] = useState(false);
  const reread = useCallback(() => {
    refetch().catch((err: unknown) => {
      console.error('ConnectionStatus: no se pudo releer la conexión y la batería', err);
    });
  }, [refetch]);

  if (!data) {
    if (error) return null;
    return (
      <Strip>
        <Skeleton width={90} />
        <Skeleton width={56} />
      </Strip>
    );
  }

  const { gate, state } = data.gateMovement;
  const { lastConnection: connection, lastBattery } = gate.movements2;
  const batteryLevel = lastBattery?.value ?? null;
  // `!== null` y no falsy: una batería en 0 es un dato, y de los graves.
  const low = batteryLevel !== null && batteryLevel < BATTERY_LOW_THRESHOLD;
  const connected = connection?.connected === true;

  return (
    <Strip>
      {state === GateMovementState2.InQueue && (
        // Sin consulta al equipo la tira sigue con lo registrado: un error acá no sube al modal.
        <ApolloErrorBoundary fallback={NoFallback}>
          <Suspense fallback={null}>
            <LiveStatusProbe gateId={gate.id} onAwaitingChange={setAwaiting} onAnswered={reread} />
          </Suspense>
        </ApolloErrorBoundary>
      )}
      <Item>
        {awaiting ? (
          <AwaitingDevice />
        ) : (
          <>
            {/* El halo del punto conectado es `--au-success-border` (el 32% del tono), no su
                relleno: es un anillo, y el `-bg` al 12% no se ve contra la cabecera. */}
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: connected ? AU_SUCCESS : AU_TEXT_DIM,
                boxShadow: connected ? `0 0 0 3px ${AU_TINT.success.border}` : 'none',
              }}
            />
            {connection == null ? (
              'N/D'
            ) : connected ? (
              <Trans>conectado</Trans>
            ) : (
              <Trans>desconectado</Trans>
            )}
          </>
        )}
      </Item>

      {/* El color de severidad va solo en el glifo, en `--au-error-text`: el porcentaje es el dato
          exacto y queda en `--au-text`. */}
      <Item>
        {batteryLevel !== null ? (
          <BatteryLevelIcon
            battery={batteryLevel}
            sx={{ fontSize: 16, color: low ? AU_TINT.error.text : undefined }}
          />
        ) : (
          <BatteryUnknown sx={{ fontSize: 16 }} />
        )}
        {lastBattery
          ? formatMetricValue(lastBattery.value, {
              unit: lastBattery.unit,
              precision: lastBattery.precision,
            })
          : 'N/D'}
      </Item>
    </Strip>
  );
};

/**
 * Consulta al equipo una vez al montar, si el usuario puede operar; al responder, `onAnswered`
 * relee la caché. Aparte de la tira porque el permiso es una suspense query y no debe remontarla.
 */
const LiveStatusProbe: FC<{
  gateId: string;
  onAwaitingChange: (awaiting: boolean) => void;
  onAnswered: () => void;
}> = ({ gateId, onAwaitingChange, onAnswered }) => {
  const { validateAccess } = useSuspenseAuthorization(gateId);
  const { awaiting } = useGateLiveStatus(validateAccess(Access.OperateGate) ? gateId : null);
  const asked = useRef(false);

  useEffect(() => {
    onAwaitingChange(awaiting);
    if (awaiting) asked.current = true;
    else if (asked.current) {
      asked.current = false;
      onAnswered();
    }
  }, [awaiting, onAwaitingChange, onAnswered]);
  useEffect(() => () => onAwaitingChange(false), [onAwaitingChange]);

  return null;
};

const Strip: FC<{ children: ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>{children}</Box>
);

/** `.oc-conn-item` — 11,5px/600, con 6px entre la marca y su palabra. */
const Item: FC<ComponentProps<typeof Box>> = ({ sx, ...props }) => (
  <Box
    {...props}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: 11.5,
      fontWeight: 600,
      whiteSpace: 'nowrap',
      ...sx,
    }}
  />
);
